import type {
  CopilotChatReference,
  FileDiffReference,
  FileReference,
  SnippetReference,
} from '@github-ui/copilot-chat-types'
import type {LineRange} from '@github-ui/diffs/types'
import {CopilotIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {useMemo} from 'react'
import {DiffLinesAttachMenuItem} from '../shared/DiffLinesAttachMenuItem'
import {DiffLinesExplainMenuItem} from '../shared/DiffLinesExplainMenuItem'

export interface CopilotDiffChatContextMenuProps {
  fileDiffReference: FileDiffReference
  selectedRange?: LineRange
  selectedLines?: {
    selectedLeftLines: ReadonlyArray<{left: number | null; right: number | null} | string>
    selectedRightLines?: ReadonlyArray<{left: number | null; right: number | null} | string>
  }
  showDivider?: boolean
}

function snippetReference(file: FileReference, range: {start: number; end: number}): SnippetReference {
  return {...file, type: 'snippet', range}
}

function selectedSideRange(
  lines: ReadonlyArray<{left: number | null; right: number | null} | string>,
  side: 'left' | 'right',
): {start: number; end: number} | undefined {
  const lineNumbers = lines.flatMap(line => {
    if (typeof line === 'string') return []
    const lineNumber = line[side]
    return lineNumber === null ? [] : [lineNumber]
  })
  if (lineNumbers.length === 0) return undefined
  return {start: Math.min(...lineNumbers), end: Math.max(...lineNumbers)}
}

function selectionIncludesSide(
  selectedRange: LineRange | undefined,
  lines: ReadonlyArray<{left: number | null; right: number | null} | string>,
  side: 'left' | 'right',
): boolean {
  if (selectedRange?.startOrientation === side || selectedRange?.endOrientation === side) return true

  const oppositeSide = side === 'left' ? 'right' : 'left'
  return lines.some(line => typeof line !== 'string' && line[side] !== null && line[oppositeSide] === null)
}

export function diffReferencesForSelection(
  fileDiffReference: FileDiffReference,
  selectedRange?: LineRange,
  selectedLines?: CopilotDiffChatContextMenuProps['selectedLines'],
): CopilotChatReference[] {
  const referenceWithRange = {
    ...fileDiffReference,
    ...(selectedRange
      ? {
          selectedRange: {
            start: `${selectedRange.startOrientation[0]?.toUpperCase() ?? ''}${selectedRange.startLineNumber}`,
            end: `${selectedRange.endOrientation[0]?.toUpperCase() ?? ''}${selectedRange.endLineNumber}`,
          },
        }
      : {}),
  }
  if (!selectedLines) return [referenceWithRange]

  const references: SnippetReference[] = []
  const leftRange = selectionIncludesSide(selectedRange, selectedLines.selectedLeftLines, 'left')
    ? selectedSideRange(selectedLines.selectedLeftLines, 'left')
    : undefined
  if (leftRange && fileDiffReference.baseFile) {
    references.push(snippetReference(fileDiffReference.baseFile, leftRange))
  }
  const selectedRightLines = selectedLines.selectedRightLines ?? []
  const rightRange = selectionIncludesSide(selectedRange, selectedRightLines, 'right')
    ? selectedSideRange(selectedRightLines, 'right')
    : undefined
  if (rightRange && fileDiffReference.headFile) {
    references.push(snippetReference(fileDiffReference.headFile, rightRange))
  }
  return references.length > 0 ? references : [referenceWithRange]
}

// actual contents
export const CopilotDiffChatContextMenu: React.FC<CopilotDiffChatContextMenuProps> = ({
  fileDiffReference,
  selectedRange,
  selectedLines,
  showDivider,
}) => {
  const references = useMemo(
    () => diffReferencesForSelection(fileDiffReference, selectedRange, selectedLines),
    [fileDiffReference, selectedLines, selectedRange],
  )

  return (
    <>
      <ActionMenu>
        <ActionMenu.Anchor>
          <ActionList.Item>
            <ActionList.LeadingVisual>
              <CopilotIcon />
            </ActionList.LeadingVisual>
            Copilot
          </ActionList.Item>
        </ActionMenu.Anchor>

        <ActionMenu.Overlay>
          <ActionList>
            <DiffLinesExplainMenuItem references={references} eventContext={{prx: true}} />
            <DiffLinesAttachMenuItem references={references} eventContext={{prx: true}} />
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {showDivider && <ActionList.Divider />}
    </>
  )
}
